import React, { useEffect, useState } from 'react';
import { getAdminAnalytics, getAdminEvents, getAdminUsers, approveEvent, updateUserRole } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Users,
  Calendar,
  DollarSign,
  Ticket,
  CheckCircle,
  XCircle,
  Eye,
  Shield,
  TrendingUp,
  BarChart3,
  PieChart,
  Download,
  Search,
  MessageSquare,
  Mail,
  Phone,
  Clock,
  AlertTriangle,
  HelpCircle
} from 'lucide-react';

const AdminPanel = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchUsers, setSearchUsers] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [analyticsRes, eventsRes, usersRes] = await Promise.all([
        getAdminAnalytics(),
        getAdminEvents(),
        getAdminUsers()
      ]);
      setAnalytics(analyticsRes.data);
      setEvents(eventsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveEvent = async (eventId) => {
    try {
      await approveEvent(eventId);
      toast.success('Evento aprobado');
      loadData();
    } catch (error) {
      toast.error('Error al aprobar evento');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await updateUserRole(userId, newRole);
      toast.success('Rol actualizado');
      loadData();
    } catch (error) {
      toast.error('Error al actualizar rol');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { variant: 'outline', label: 'Borrador' },
      pending: { variant: 'secondary', label: 'Pendiente', className: 'bg-yellow-500/20 text-yellow-600' },
      approved: { variant: 'default', label: 'Aprobado', className: 'bg-green-500/20 text-green-600' },
      published: { variant: 'default', label: 'Publicado', className: 'bg-green-500/20 text-green-600' },
      cancelled: { variant: 'destructive', label: 'Cancelado' }
    };
    const { variant, label, className } = config[status] || config.draft;
    return <Badge variant={variant} className={className}>{label}</Badge>;
  };

  const getRoleBadge = (role) => {
    const config = {
      buyer: { label: 'Comprador', className: 'bg-blue-500/20 text-blue-600' },
      organizer: { label: 'Organizador', className: 'bg-purple-500/20 text-purple-600' },
      staff: { label: 'Staff', className: 'bg-orange-500/20 text-orange-600' },
      admin: { label: 'Admin', className: 'bg-red-500/20 text-red-600' }
    };
    const { label, className } = config[role] || config.buyer;
    return <Badge className={className}>{label}</Badge>;
  };

  const filteredEvents = filterStatus === 'all'
    ? events
    : events.filter(e => e.status === filterStatus);

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchUsers.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchUsers.toLowerCase())
  );

  const pendingEventsCount = events.filter(e => e.status === 'pending').length;
  const organizersCount = users.filter(u => u.role === 'organizer').length;

  if (loading) {
    return (
      <div className="min-h-screen pt-20 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="h-8 w-64 bg-muted animate-pulse rounded mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-muted animate-pulse rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12 px-4" data-testid="admin-panel">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Shield className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Panel de Administración</h1>
              <p className="text-muted-foreground">MasterTicketHN.com</p>
            </div>
          </div>
        </div>

        {/* Alert for pending events */}
        {pendingEventsCount > 0 && (
          <Card className="mb-6 border-yellow-500/50 bg-yellow-500/10">
            <CardContent className="p-4 flex items-center gap-4">
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
              <div className="flex-1">
                <p className="font-medium">Tienes {pendingEventsCount} evento{pendingEventsCount > 1 ? 's' : ''} pendiente{pendingEventsCount > 1 ? 's' : ''} de aprobación</p>
                <p className="text-sm text-muted-foreground">Revisa y aprueba los eventos para que puedan ser publicados</p>
              </div>
              <Button size="sm" onClick={() => { setActiveTab('events'); setFilterStatus('pending'); }}>
                Ver Pendientes
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="glass-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Usuarios Totales</CardTitle>
              <Users className="h-5 w-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{analytics?.total_users || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {organizersCount} organizadores
              </p>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Eventos</CardTitle>
              <Calendar className="h-5 w-5 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{analytics?.total_events || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {pendingEventsCount} pendientes de aprobación
              </p>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Ingresos Totales</CardTitle>
              <DollarSign className="h-5 w-5 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">L.{analytics?.total_revenue?.toFixed(2) || '0.00'}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Todas las ventas
              </p>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Comisiones</CardTitle>
              <TrendingUp className="h-5 w-5 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">L.{analytics?.total_fees?.toFixed(2) || '0.00'}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {analytics?.total_orders || 0} órdenes procesadas
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <BarChart3 className="w-4 h-4 mr-2" />
              Resumen
            </TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events">
              <Calendar className="w-4 h-4 mr-2" />
              Eventos
              {pendingEventsCount > 0 && (
                <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  {pendingEventsCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">
              <Users className="w-4 h-4 mr-2" />
              Usuarios
            </TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">
              <PieChart className="w-4 h-4 mr-2" />
              Reportes
            </TabsTrigger>
            <TabsTrigger value="support" data-testid="tab-support">
              <MessageSquare className="w-4 h-4 mr-2" />
              Soporte
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Events by Status */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle>Eventos por Estado</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(analytics?.events_by_status || {}).map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {getStatusBadge(status)}
                        </div>
                        <span className="font-bold text-lg">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle>Estadísticas Rápidas</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <span className="text-muted-foreground">Promedio por orden</span>
                      <span className="font-bold">
                        L.{analytics?.total_orders > 0 
                          ? (analytics.total_revenue / analytics.total_orders).toFixed(2) 
                          : '0.00'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <span className="text-muted-foreground">Tickets por evento (promedio)</span>
                      <span className="font-bold">
                        {analytics?.total_events > 0 
                          ? Math.round(events.reduce((sum, e) => sum + e.sold_tickets, 0) / analytics.total_events)
                          : 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <span className="text-muted-foreground">Tasa de conversión (comisión)</span>
                      <span className="font-bold">5%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <Card className="glass-card">
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <CardTitle>Gestión de Eventos</CardTitle>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Filtrar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="pending">Pendientes</SelectItem>
                      <SelectItem value="approved">Aprobados</SelectItem>
                      <SelectItem value="published">Publicados</SelectItem>
                      <SelectItem value="draft">Borradores</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Evento</TableHead>
                      <TableHead>Organizador</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Ventas</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEvents.map((event) => (
                      <TableRow key={event.event_id} data-testid={`admin-event-${event.event_id}`}>
                        <TableCell>
                          <div className="font-medium">{event.title}</div>
                          <div className="text-xs text-muted-foreground capitalize">{event.category}</div>
                        </TableCell>
                        <TableCell>
                          <div>{event.organizer?.name || 'N/A'}</div>
                          <div className="text-xs text-muted-foreground">{event.organizer?.email}</div>
                        </TableCell>
                        <TableCell>
                          {format(new Date(event.date), 'd MMM yyyy', { locale: es })}
                        </TableCell>
                        <TableCell>{getStatusBadge(event.status)}</TableCell>
                        <TableCell>
                          <span className="font-medium">{event.sold_tickets}</span>
                          <span className="text-muted-foreground">/{event.total_tickets}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {event.status === 'pending' && (
                              <Button
                                size="sm"
                                onClick={() => handleApproveEvent(event.event_id)}
                                className="bg-green-600 hover:bg-green-700"
                                data-testid={`approve-${event.event_id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Aprobar
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" asChild>
                              <a href={`/events/${event.event_id}`} target="_blank" rel="noopener noreferrer">
                                <Eye className="w-4 h-4" />
                              </a>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="glass-card">
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <CardTitle>Gestión de Usuarios</CardTitle>
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar usuarios..."
                      value={searchUsers}
                      onChange={(e) => setSearchUsers(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Usuario</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Rol Actual</TableHead>
                      <TableHead>Registrado</TableHead>
                      <TableHead className="text-right">Cambiar Rol</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((u) => (
                      <TableRow key={u.user_id} data-testid={`admin-user-${u.user_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {u.picture ? (
                              <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <Users className="w-4 h-4 text-primary" />
                              </div>
                            )}
                            <span className="font-medium">{u.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{getRoleBadge(u.role)}</TableCell>
                        <TableCell>
                          {u.created_at ? format(new Date(u.created_at), 'd MMM yyyy', { locale: es }) : 'N/A'}
                        </TableCell>
                        <TableCell className="text-right">
                          {u.user_id !== user?.user_id && (
                            <Select
                              value={u.role}
                              onValueChange={(value) => handleUpdateRole(u.user_id, value)}
                            >
                              <SelectTrigger className="w-36">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="buyer">Comprador</SelectItem>
                                <SelectItem value="organizer">Organizador</SelectItem>
                                <SelectItem value="staff">Staff</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle>Reporte de Ventas</CardTitle>
                  <CardDescription>Resumen general de ventas de la plataforma</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                        <p className="text-sm text-muted-foreground">Ingresos Brutos</p>
                        <p className="text-2xl font-bold text-green-600">L.{analytics?.total_revenue?.toFixed(2) || '0.00'}</p>
                      </div>
                      <div className="p-4 rounded-xl bg-orange-500/10 border border-orange-500/20">
                        <p className="text-sm text-muted-foreground">Comisiones (5%)</p>
                        <p className="text-2xl font-bold text-orange-600">L.{analytics?.total_fees?.toFixed(2) || '0.00'}</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Órdenes procesadas</span>
                        <span className="font-bold">{analytics?.total_orders || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Ticket promedio</span>
                        <span className="font-bold">
                          L.{analytics?.total_orders > 0 
                            ? (analytics.total_revenue / analytics.total_orders).toFixed(2)
                            : '0.00'}
                        </span>
                      </div>
                    </div>
                    <Button className="w-full" variant="outline">
                      <Download className="w-4 h-4 mr-2" />
                      Descargar Reporte CSV
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-card">
                <CardHeader>
                  <CardTitle>Reporte de Eventos</CardTitle>
                  <CardDescription>Análisis de eventos en la plataforma</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="space-y-3">
                      {Object.entries(analytics?.events_by_status || {}).map(([status, count]) => {
                        const total = analytics?.total_events || 1;
                        const percentage = Math.round((count / total) * 100);
                        return (
                          <div key={status}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="capitalize">{status}</span>
                              <span>{count} ({percentage}%)</span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-primary rounded-full transition-all"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <Button className="w-full" variant="outline">
                      <Download className="w-4 h-4 mr-2" />
                      Descargar Reporte PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-card lg:col-span-2">
                <CardHeader>
                  <CardTitle>Top Eventos por Ventas</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>Evento</TableHead>
                        <TableHead>Organizador</TableHead>
                        <TableHead>Tickets Vendidos</TableHead>
                        <TableHead>Ingresos</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {events
                        .sort((a, b) => b.sold_tickets - a.sold_tickets)
                        .slice(0, 5)
                        .map((event, index) => (
                          <TableRow key={event.event_id}>
                            <TableCell className="font-bold">{index + 1}</TableCell>
                            <TableCell className="font-medium">{event.title}</TableCell>
                            <TableCell>{event.organizer?.name || 'N/A'}</TableCell>
                            <TableCell>{event.sold_tickets}</TableCell>
                            <TableCell className="font-medium">
                              L.{(event.ticket_types?.reduce((sum, t) => sum + (t.price * t.sold), 0) || 0).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-primary" />
                    Soporte por Email
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Revisa y responde solicitudes de soporte enviadas por email.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <span>Tickets abiertos</span>
                      <Badge>0</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <span>Tiempo de respuesta promedio</span>
                      <span className="font-medium">2h</span>
                    </div>
                  </div>
                  <Button className="w-full mt-4" variant="outline">
                    Abrir Bandeja de Entrada
                  </Button>
                </CardContent>
              </Card>

              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-green-500" />
                    Chat en Vivo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Atiende a usuarios en tiempo real mediante chat.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <span>Chats activos</span>
                      <Badge variant="secondary">0</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <span>En espera</span>
                      <Badge variant="outline">0</Badge>
                    </div>
                  </div>
                  <Button className="w-full mt-4 bg-green-600 hover:bg-green-700">
                    Iniciar Chat
                  </Button>
                </CardContent>
              </Card>

              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-blue-500" />
                    Centro de Ayuda
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Gestiona artículos de ayuda y FAQs para usuarios.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <span>Artículos publicados</span>
                      <span className="font-medium">12</span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <span>FAQs activas</span>
                      <span className="font-medium">8</span>
                    </div>
                  </div>
                  <Button className="w-full mt-4" variant="outline">
                    Gestionar Contenido
                  </Button>
                </CardContent>
              </Card>

              {/* Recent Support Activity */}
              <Card className="glass-card lg:col-span-3">
                <CardHeader>
                  <CardTitle>Actividad de Soporte Reciente</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No hay solicitudes de soporte recientes</p>
                    <p className="text-sm">Las nuevas solicitudes aparecerán aquí</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
